import { useEffect, useMemo, useState } from 'react'
import Alert from 'react-s-alert'
import { Button, Card, InfoTooltip } from 'shared'
import getMfaApi from '../../api/mfa'
import CustomIcon from '../../common/CustomIcon'
import { COMPANY_PHONE_NUMBER } from '../../components/contact-us-modal/ContactUsModal'
import LoadingSpinner from '../../components/loading-spinner/LoadingSpinner'
import Modal from '../../components/modal/Modal'
import './MfaSetting.scss'

type MfaSettingProps = {
  authToken: string
  userid: string
  onLogout: () => void
}

const MfaSetting = ({ authToken, userid, onLogout }: MfaSettingProps) => {
  const [isMfaActive, setIsMfaActive] = useState(false)
  const [isMfaLoading, setIsMfaLoading] = useState(true)
  const [showMfaInfoModal, setShowMfaInfoModal] = useState(false)
  const [showMfaActiveModal, setShowMfaActiveModal] = useState(false)

  const isMfaEnabled = process.env.REACT_APP_FEATURE_ENABLE_MFA === 'true'

  const mfaApi = useMemo(() => {
    if (!isMfaEnabled || !authToken) {
      return null
    }
    return getMfaApi(authToken)
  }, [authToken]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mfaApi) {
      return
    }

    mfaApi.getMfa(userid).then(({ data }) => {
      setIsMfaActive(!!data?.mfaOptIn)
      setIsMfaLoading(false)
    })
  }, [mfaApi, userid])

  const onActivateMfa = () => {
    setIsMfaLoading(true)
    mfaApi.activateMfa(userid).then(({ status }) => {
      if (status >= 200 && status < 300) {
        setIsMfaActive(true)
        setShowMfaActiveModal(true)
      } else {
        Alert.error(
          `There was an error activating MFA please try again later or contact ${COMPANY_PHONE_NUMBER}`
        )
      }
      setIsMfaLoading(false)
    })
  }

  const renderMfaAction = () => {
    const shieldIcon = <CustomIcon icon="shield-check" className="mr-xs" />
    if (isMfaLoading) {
      return (
        <div className="mfa-action-status input-tooltip-group color-dusk text-large">
          {shieldIcon}
          <LoadingSpinner className="color-inherit" size={16} />
        </div>
      )
    }

    if (isMfaActive) {
      return (
        <div className="mfa-action-status input-tooltip-group color-dusk text-large">
          {shieldIcon}
          <InfoTooltip
            size="sm"
            tooltipContent={
              <p className="m-0">
                If you want to turn off or reset your MFA, please contact us via
                LiveChat or by calling us on{' '}
                <span className="color-seafoam">{COMPANY_PHONE_NUMBER}</span>
              </p>
            }
          >
            <span className="tooltip__label color-text-light">
              Currently ON
            </span>
          </InfoTooltip>
        </div>
      )
    }

    return (
      <Button
        className="mfa-action-status activate-mfa input-tooltip-group color-dusk text-large"
        color="transparent"
        onClick={() => onActivateMfa()}
      >
        {shieldIcon}
        <span>Activate MFA</span>
      </Button>
    )
  }

  if (!isMfaEnabled) {
    return null
  }

  return (
    <>
      <Card className="mfa-setting mb-md">
        <h5 className="mobile-sm-hidden mb-lg">Security</h5>
        <div className="mfa-container">
          <div className="flex-row">
            <CustomIcon icon="shield-key" />
            <div className="mfa-labels flex-column">
              <span className="mfa-labels__header text-large color-midnight">
                Multi-Factor Authentication (MFA)
              </span>
              <Button
                className="text-regular text-underline mt-2xs"
                variant="link"
                onClick={() => setShowMfaInfoModal(true)}
              >
                What is Multi-Factor Authentication?
              </Button>
            </div>
          </div>
          {renderMfaAction()}
        </div>
      </Card>

      {showMfaInfoModal && (
        <Modal
          className="mfa-modal"
          title="What is Multi-Factor Authentication"
          open
          onClose={() => setShowMfaInfoModal(false)}
        >
          <p className="text-large mt-0">
            Multi-factor authentication (MFA) adds an extra layer of security to
            your account. In addition to entering your usual username and
            password, MFA requires you to enter a one-time code. This code is
            generated by an authenticator app or sent to you via SMS. Since each
            code is randomly generated and the request expires within 5 minutes,
            it significantly reduces the risk of unauthorised access to your
            account.
          </p>

          <p className="text-large my-0">
            You can set up MFA in either Fisher Funds Online Portal or Mobile
            App.
          </p>

          <h5 className="mt-lg">How does it work?</h5>
          <ol className="text-large mt-0 mb-sm">
            <li>
              After logging in with your username/email and password,
              you&apos;ll be prompted to enter a login code.
            </li>
            <li>
              This code will be sent to you through the method you selected
              (either SMS or an Authenticator App).
            </li>
            <li>
              Open the Authenticator App on your mobile device or check your
              phone for a text message.
            </li>
            <li>
              Enter the code in the box on the screen. If the code is correct,
              we’ll know it’s really you logging in to your account.
            </li>
          </ol>
          <p className="text-large my-0">
            <strong className="color-midnight">Note:</strong> If you have FaceID
            enabled on your mobile app, you'll only be prompted to enter an MFA
            code when registering a new mobile device or after certain app
            updates.
          </p>

          <h5 className="mt-lg">How to activate MFA</h5>
          <ol className="text-large my-0">
            <li>
              In your <strong>My Details</strong> settings, click on{' '}
              <strong>Multi-Factor Authentication</strong> to begin setting up
              MFA. Once opted into MFA, you’ll be prompted to complete the setup
              the next time you log in to Fisher Funds Online.
            </li>
            <li>
              Choose one of the two methods for receiving the MFA login code:{' '}
              <strong>Authenticator App or SMS text message.</strong>
            </li>
            <li>
              <strong>If you choose “Authenticator App”</strong>: You’ll need to
              download an Authenticator App on your smartphone.
              <ul>
                <li>
                  After selecting "Use an app," scan the QR code displayed on
                  the screen with your Authenticator App.
                </li>
                <li>Enter the one-time password generated by the app.</li>
              </ul>
            </li>
            <li>
              <strong>If you choose SMS</strong>: Make sure the mobile number
              you provide matches the one we have on file for you.
            </li>
          </ol>
          <div className="mfa-modal-footer flex-row pb-xs">
            <Button
              className="ml-auto"
              variant="link"
              size="lg"
              onClick={() => setShowMfaInfoModal(false)}
            >
              Okay, got it
            </Button>
          </div>
        </Modal>
      )}
      {showMfaActiveModal && (
        <Modal
          className="mfa-modal"
          title="Multi-Factor Authentication (MFA)"
          open
          onClose={() => setShowMfaActiveModal(false)}
        >
          <p className="text-large my-0">
            Great! Your Multi-Factor Authentication has been enabled. You’ll go
            through the set up process the next time you log in.
          </p>
          <div className="mfa-modal-footer flex-row pb-xs">
            <Button
              className="color-puriri"
              variant="link"
              size="lg"
              onClick={() => onLogout()}
            >
              Log out & set up now
            </Button>
            <Button
              className="ml-auto"
              variant="link"
              size="lg"
              onClick={() => setShowMfaActiveModal(false)}
            >
              Okay, got it
            </Button>
          </div>
        </Modal>
      )}
    </>
  )
}

export default MfaSetting
